// dependency-graph-ignore-file
import { token } from '@atlaskit/tokens';
import { useCommentActionsContext } from '@databases/comments/hooks/useCommentContext';
import { useDialog } from '@databases/dialog';
import useView from '@databases/views/useView';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSnapshot } from 'valtio';

import { createSelectValueContextMenuItems } from '../select-value-menu/utils/create-select-value-context-menu-items';
import { contextMenuState } from '../state/context-menu-state';
import { computeMenuPosition } from '../utils/compute-menu-position';

import { ContextMenuContent } from './ContextMenuContent';

/**
 * Renders the context menu using React Portal.
 * This component is mounted inside the React tree, so it has access to all contexts.
 */
export const ContextMenuPortalRenderer: React.FC = () => {
  const { isVisible, position } = useSnapshot(contextMenuState.state);
  const { showDialog } = useDialog();
  const { setOpenThread } = useCommentActionsContext();
  const { setViewChange } = useView();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const menuData = isVisible ? contextMenuState.getMenuData() : null;
  const items =
    menuData == null
      ? []
      : createSelectValueContextMenuItems(
          menuData.context,
          menuData.actionsBuilder(setViewChange)
        );

  useEffect(() => {
    if (isVisible && menuData && items.length === 0) {
      contextMenuState.hide();
    }
  }, [isVisible, menuData, items.length]);

  useEffect(() => {
    if (!containerRef.current) {
      const container = document.createElement('div');

      container.id = 'database-context-menu-portal';
      container.dataset.interactive = 'true';

      Object.assign(container.style, {
        background: token('elevation.surface.overlay', '#fff'),
        borderRadius: token('radius.small', '4px'),
        boxShadow: 'var(--ds-shadow-overlay, var(--canvas-shadow-default))',
        display: 'none',
        pointerEvents: 'auto',
        position: 'fixed',
        transformOrigin: '0 0',
        zIndex: '600',
      });

      document.body.appendChild(container);
      containerRef.current = container;
    }

    return () => {
      containerRef.current?.remove();
      containerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    if (isVisible && position) {
      const coords = computeMenuPosition(position);

      containerRef.current.style.display = 'block';
      containerRef.current.style.transform = `translate(${coords.x}px, ${coords.y}px)`;
    } else {
      containerRef.current.style.display = 'none';
    }
  }, [isVisible, position]);

  if (!isVisible || !containerRef.current) {
    return null;
  }

  return createPortal(
    <ContextMenuContent
      items={items}
      showDialog={showDialog}
      setOpenThread={setOpenThread}
    />,
    containerRef.current
  );
};
